/** Llamadas HTTP del feature Reajustes Tarifarios — Sprint 4 SISS. */
import { apiDownload, apiRequest } from "../../lib/api";

export type EstadoReajuste =
  | "Borrador"
  | "EnviadoSISS"
  | "VistoBueno"
  | "Aplicado"
  | "Anulado";

export type Concepto =
  | "CargoFijoAgua"
  | "CargoFijoAlc"
  | "FondoReposicion"
  | "OtrosCargos"
  | "Tramo"
  | "Grifo"
  | "Corte"
  | "Reposicion"
  | "RevisionProyecto";

export type ReferenciaTipo = "TarifaBase" | "TramoTarifa" | null;

export interface DetalleReajuste {
  id: number;
  reajuste_id: number;
  concepto: Concepto;
  subconcepto: string | null;
  valor_anterior: string;
  valor_nuevo: string;
  valor_nuevo_editado: boolean;
  referencia_tipo: ReferenciaTipo;
  referencia_id: number | null;
}

export interface DetalleReajusteInput {
  concepto: Concepto;
  subconcepto?: string | null;
  valor_anterior: string;
  valor_nuevo: string;
  referencia_tipo?: ReferenciaTipo;
  referencia_id?: number | null;
}

export interface ReajustePreview {
  factor_in: string;
  variacion_pct: string;
  detalles: Array<{
    concepto: Concepto;
    subconcepto: string | null;
    valor_anterior: string;
    valor_nuevo: string;
    referencia_tipo: ReferenciaTipo;
    referencia_id: number | null;
  }>;
}

export interface Reajuste {
  id: number;
  empresa_id: number;
  tarifa_id: number;
  ipc_0_valor: string;
  ipc_0_mes: string;
  ipc_actual_valor: string;
  ipc_actual_mes: string;
  factor_in: string;
  variacion_pct: string;
  fecha_vigencia: string;
  localidades_afectadas: string;
  estado: EstadoReajuste;
  fecha_envio_siss: string | null;
  fecha_visto_bueno_siss: string | null;
  numero_visto_bueno: string | null;
  fecha_aplicacion: string | null;
  motivo_anulacion: string | null;
  observaciones: string | null;
  created_by_id: number | null;
  aplicado_por_id: number | null;
  created_at: string;
  updated_at: string;
  detalles: DetalleReajuste[];
}

export interface ReajusteListResponse {
  items: Reajuste[];
  total: number;
  limit: number;
  offset: number;
}

export interface CalcularInput {
  tarifa_id: number;
  ipc_0_valor: string;
  ipc_0_mes: string;
  ipc_actual_valor: string;
  ipc_actual_mes: string;
}

export interface ReajusteCreateInput extends CalcularInput {
  fecha_vigencia: string;
  localidades_afectadas: string;
  observaciones?: string | null;
  detalles: DetalleReajusteInput[];
}

export function calcularPreview(payload: CalcularInput): Promise<ReajustePreview> {
  return apiRequest<ReajustePreview>("/api/v1/reajustes/calcular", {
    method: "POST",
    body: payload,
  });
}

export function fetchReajustes(
  filters: { estado?: EstadoReajuste | null; tarifa_id?: number | null } = {},
): Promise<ReajusteListResponse> {
  const params = new URLSearchParams();
  if (filters.estado) params.set("estado", filters.estado);
  if (filters.tarifa_id) params.set("tarifa_id", String(filters.tarifa_id));
  const qs = params.toString();
  return apiRequest<ReajusteListResponse>(
    `/api/v1/reajustes${qs ? `?${qs}` : ""}`,
  );
}

export function fetchReajuste(id: number): Promise<Reajuste> {
  return apiRequest<Reajuste>(`/api/v1/reajustes/${id}`);
}

export function crearReajuste(payload: ReajusteCreateInput): Promise<Reajuste> {
  return apiRequest<Reajuste>("/api/v1/reajustes", {
    method: "POST",
    body: payload,
  });
}

export function enviarSiss(
  id: number,
  fechaEnvio?: string,
): Promise<Reajuste> {
  return apiRequest<Reajuste>(`/api/v1/reajustes/${id}/enviar`, {
    method: "POST",
    body: { fecha_envio_siss: fechaEnvio || null },
  });
}

export function registrarVistoBueno(
  id: number,
  numeroVistoBueno: string,
  fechaVistoBueno?: string,
): Promise<Reajuste> {
  return apiRequest<Reajuste>(`/api/v1/reajustes/${id}/visto-bueno`, {
    method: "POST",
    body: {
      numero_visto_bueno: numeroVistoBueno,
      fecha_visto_bueno_siss: fechaVistoBueno || null,
    },
  });
}

export function aplicarReajuste(id: number): Promise<Reajuste> {
  return apiRequest<Reajuste>(`/api/v1/reajustes/${id}/aplicar`, {
    method: "POST",
  });
}

export function anularReajuste(id: number, motivo: string): Promise<Reajuste> {
  return apiRequest<Reajuste>(`/api/v1/reajustes/${id}/anular`, {
    method: "POST",
    body: { motivo },
  });
}

export function descargarInformePdf(id: number): Promise<void> {
  return apiDownload(
    `/api/v1/reajustes/${id}/informe.pdf`,
    `reajuste_tarifario_${id}.pdf`,
  );
}

export const ESTADO_LABELS: Record<EstadoReajuste, string> = {
  Borrador: "Borrador",
  EnviadoSISS: "Enviado a SISS",
  VistoBueno: "Visto bueno SISS",
  Aplicado: "Aplicado",
  Anulado: "Anulado",
};

export const CONCEPTO_LABELS: Record<Concepto, string> = {
  CargoFijoAgua: "Cargo fijo (Agua Potable)",
  CargoFijoAlc: "Cargo fijo (Saneamiento)",
  FondoReposicion: "Fondo Reposición y Reinversión",
  OtrosCargos: "Otros cargos fijos",
  Tramo: "Cargo variable Agua Potable (tramo)",
  Grifo: "Grifos",
  Corte: "Cortes",
  Reposicion: "Reposiciones",
  RevisionProyecto: "Revisión proyectos / Informes",
};
